/**
 * Reusable validation utilities for form fields across the application.
 */

const DRIVERS_LICENSE_REGEX = /^[A-Za-z][0-9]{7}$/;
const DRIVERS_LICENSE_ERROR = "Driver's License Number must be 1 letter followed by 7 digits (e.g., D1234567)";

/**
 * Validates a Driver's License Number.
 *
 * Rules:
 * - First character must be a letter (A–Z or a–z).
 * - Next 7 characters must be numeric digits (0–9).
 * - Total length must be exactly 8 characters.
 * - No special characters or spaces allowed.
 *
 * @param value - The raw input value.
 * @returns An error message string if invalid, or `null` if valid / empty.
 */
export function validateDriversLicense(value: string | null | undefined): string | null {
  if (!value || !value.trim()) return null; // empty is not an error (field may be optional)

  const trimmed = value.trim();
  if (!DRIVERS_LICENSE_REGEX.test(trimmed)) {
    return DRIVERS_LICENSE_ERROR;
  }
  return null;
}

/**
 * Sanitises Driver's License input: strips everything except letters and digits,
 * auto-uppercases the first character, and enforces max length of 8.
 */
export function sanitizeDriversLicense(value: string): string {
  // Remove anything that isn't a letter or digit
  let cleaned = value.replace(/[^A-Za-z0-9]/g, '');

  // Auto-uppercase the first character
  if (cleaned.length > 0) {
    cleaned = cleaned[0].toUpperCase() + cleaned.slice(1);
  }

  // Enforce max 8 characters
  return cleaned.slice(0, 8);
}

// ── Phone Number Utilities ──

const PHONE_DIGITS_ONLY = /\D/g;
const PHONE_ERROR = 'Phone number must be 10 digits (e.g., 123-456-7890)';

/**
 * Extracts raw digits from a formatted or raw phone value.
 * Use this before sending phone data to the backend.
 *
 * @example unformatPhone('123-456-7890') => '1234567890'
 * @example unformatPhone('1234567890')   => '1234567890'
 */
export function unformatPhone(value: string | null | undefined): string {
  if (!value) return '';
  return value.replace(PHONE_DIGITS_ONLY, '');
}

/**
 * Formats a raw digit string into the display mask `123-456-7890`.
 * Inserts dashes dynamically as the user types.
 *
 * @example formatPhone('123')        => '123'
 * @example formatPhone('1234')       => '123-4'
 * @example formatPhone('1234567890') => '123-456-7890'
 */
export function formatPhone(value: string | null | undefined): string {
  if (!value) return '';
  const digits = value.replace(PHONE_DIGITS_ONLY, '').slice(0, 10);

  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
}

/**
 * Sanitises phone input: strips non-digits and caps at 10 characters.
 * Returns **raw digits only** (for storage in form state).
 */
export function sanitizePhone(value: string): string {
  return value.replace(PHONE_DIGITS_ONLY, '').slice(0, 10);
}

/**
 * Validates a phone number (raw digits).
 *
 * @param value - Raw digits (e.g. '1234567890') or formatted (e.g. '123-456-7890').
 * @param required - Whether the field is required. Defaults to false.
 * @returns An error message string if invalid, or `null` if valid.
 */
export function validatePhone(value: string | null | undefined, required = false): string | null {
  const digits = unformatPhone(value);
  if (!digits) return required ? 'Phone number is required' : null;
  if (digits.length !== 10) return PHONE_ERROR;
  return null;
}
